import type { FlowRunNodeState } from "@/api/flow-run-dag";
import type { LifecycleStateLabel } from "@/api/projects";
import { ACTIVE_RUN_STATES } from "@/api/runs";
import { STATE_LABEL } from "@/components/runs/run-state-label";

/**
 * **The per-tile progress model (VIS/V1).** What each Tile of a flow run is
 * doing right now, and the rollup over all of them.
 *
 * Every field here is a FACT read off `GET /api/runs/{id}/flow-dag`. The one
 * thing this module deliberately does not produce is a completion FRACTION for
 * a tile that is still running, and that absence is the point — see
 * {@link TileProgressRow.determinate}.
 *
 * ---
 *
 * ## What the wire actually supports, per field
 *
 * The backend (`server/routes/dag.py::_project_run_scoped_nodes`) collapses a
 * node's cohort of sibling runs to one row, and the collapse rules are what
 * make the claims below safe to render:
 *
 * - **`status` on a fanned node is the LEAST-progressed sibling.**
 *   `_AGGREGATE_PRECEDENCE` is worst-first, so `completed` on a ×6 node means
 *   all six completed — never "one finished and five are pending". That is what
 *   lets {@link tileProgressRollup} count completed tiles honestly.
 * - **`child_count` is the fan-out width in THIS run's cohort**, counted from
 *   real dispatched rows, not read off the template. `0` is not "none" until
 *   `expansion_reason` says which kind of none it is (flowstack#663).
 * - **`duration_seconds` is meaningful only at `child_count === 1`** — the
 *   backend sends `null` for a fanned node, because six siblings have six
 *   durations. This module drops it for every other case rather than passing a
 *   number whose subject is unclear.
 * - **`cache_hit` on a fanned node is `any(child cached)`**, so the rollup's
 *   cached count is "tiles with at least one cache hit". The copy in
 *   `RunLifecycle.tsx` says exactly that; a bare "4 cached" would read as four
 *   whole tiles served from cache.
 *
 * ## Why no per-tile percentage
 *
 * There is no completion fraction anywhere on this payload for a running tile,
 * and there is no honest way to derive one. Elapsed-over-expected would need an
 * expected duration nothing records. Children-done-over-`child_count` would need
 * the per-sibling breakdown, which lives on a DIFFERENT, flow-template-keyed
 * endpoint (`/flows/{name}/nodes/{node}/children`) — one request per fanned node
 * per poll, which is the request storm this unit is required not to create.
 *
 * So a running tile gets an INDETERMINATE bar: `determinate: false`, no
 * `aria-valuenow`, and a track whose motion says "working" without ever
 * resolving to a number a reader could quote. An estimate would read as a fact
 * (`flowstack#1089` finding 1); a bare bar at some arbitrary fill would be that
 * same defect drawn instead of printed.
 */

/** The bucket a tile row is drawn in — closed, and not a lifecycle label.
 *
 * Four display buckets over the twelve lifecycle states, named for what the
 * reader is scanning for. They are a VIEW, never a state: `status` on the row
 * keeps the canonical label, and every piece of copy that names a state uses
 * that (INDEX.md invariant #3 — never invent a run-state string). */
export type TilePhase = "done" | "cached" | "active" | "failed" | "queued";

export interface TileProgressRow {
  /** Flow node name — the DAG's own identifier, in mono at the call site. */
  name: string;
  /** The Tile this node instantiates. */
  tile: string;
  /** `null` when the node has no run in this cohort yet. */
  status: LifecycleStateLabel | null;
  /** Display text for {@link status}, or "queued" when there is none. */
  label: string;
  phase: TilePhase;
  /** Fan-out width, `null` for a singleton or an unexpanded node — a `×1` chip
   * is noise, since one child is what an ordinary node has. */
  fanoutWidth: number | null;
  fanoutKind: "scope" | "match" | null;
  /** Wall seconds, only where the backend's value has one subject
   * (`child_count === 1`). `null` otherwise, rendered as an em dash. */
  durationSeconds: number | null;
  /**
   * `false` for every running tile: there is a bar, and it carries no value.
   * Always `false` today; it is a FIELD rather than a hardcoded `false` at the
   * call site so that the day a real fraction lands on the wire, the view knows
   * where to put it and the reader learns the difference in one place.
   */
  determinate: false;
  /** Why this node has no children, when that needs saying. `null` when the
   * count speaks for itself. */
  note: string | null;
}

export interface TileProgressRollup {
  /** Structural node count — every node of the flow, whether reached or not. */
  total: number;
  done: number;
  cached: number;
  active: number;
  failed: number;
  queued: number;
  /** Tiles with at least one cache hit among their children. NOT a subset
   * count of {@link done}: a cached node's status IS `cached`. */
  cacheHits: number;
}

const PHASE_BY_STATUS = (status: LifecycleStateLabel): TilePhase => {
  if (status === "completed") return "done";
  if (status === "cached") return "cached";
  return ACTIVE_RUN_STATES.has(status) ? "active" : "failed";
};

/**
 * Why a node has no children, in the reader's words.
 *
 * Only the two cases a count cannot express get a note. `expanded` is the
 * ordinary case and `not_expanded` is what "queued" already says — a sentence
 * on either would be chrome repeating the row beside it.
 *
 * `expansion_failed` deliberately does NOT assume `child_count === 0`: a node
 * can dispatch some children and only then hit the fault that stopped the rest,
 * so the note says the expansion failed and leaves the count to the chip.
 */
function expansionNote(node: FlowRunNodeState): string | null {
  switch (node.expansion_reason) {
    case "no_upstream_items":
      return "Nothing to fan out over — its source list was empty. Nothing failed.";
    case "expansion_failed":
      return node.expansion_detail
        ? `Fan-out failed: ${node.expansion_detail}`
        : "Fan-out failed before every child was dispatched.";
    default:
      return null;
  }
}

/**
 * One row per structural node, in the order the endpoint sends them.
 *
 * That order is the backend's topological sort, which is the pipeline read this
 * view exists to give — prepare, then enumerate, then scan, then fit. It is
 * deliberately NOT re-sorted here: `RunWorkspace`'s `FlowRunNodeTable` is the
 * sortable audit view of the same nodes and defaults to alphabetical, which
 * answers "where is node X" well and "how far along is this run" not at all.
 * The two read the SAME query key, so this second view costs no extra request.
 */
export function tileProgressRows(nodes: readonly FlowRunNodeState[]): TileProgressRow[] {
  return nodes.map((node) => ({
    name: node.name,
    tile: node.tile,
    status: node.status,
    label: node.status === null ? "queued" : STATE_LABEL[node.status],
    phase: node.status === null ? "queued" : PHASE_BY_STATUS(node.status),
    fanoutWidth: node.child_count > 1 ? node.child_count : null,
    fanoutKind: node.fanout_kind,
    durationSeconds: node.child_count === 1 ? node.duration_seconds : null,
    determinate: false,
    note: expansionNote(node),
  }));
}

/** Counts over the same rows — every one a count of TILES, never of work. */
export function tileProgressRollup(nodes: readonly FlowRunNodeState[]): TileProgressRollup {
  const rollup: TileProgressRollup = {
    total: nodes.length,
    done: 0,
    cached: 0,
    active: 0,
    failed: 0,
    queued: 0,
    cacheHits: 0,
  };
  for (const node of nodes) {
    if (node.cache_hit === true) rollup.cacheHits += 1;
    if (node.status === null) {
      rollup.queued += 1;
      continue;
    }
    switch (PHASE_BY_STATUS(node.status)) {
      case "done":
        rollup.done += 1;
        break;
      case "cached":
        rollup.cached += 1;
        break;
      case "active":
        rollup.active += 1;
        break;
      default:
        rollup.failed += 1;
    }
  }
  return rollup;
}

/**
 * The rollup as the header sentence — "6 of 14 tiles settled · 3 running".
 *
 * "Settled", not "complete": the figure counts `completed` AND `cached` tiles,
 * because a cache hit is a tile that will not run again and calling it
 * incomplete would understate the run. Failures are named separately rather
 * than folded in, since a reader scanning a live run needs to see one.
 *
 * The counts are of TILES. `RunLifecycle.tsx` says so beside them, because
 * "6 of 14" invites reading as 43% done and tiles are nowhere near equal in
 * cost — `constrained_opt` can run tens of minutes per child while
 * `prepare_ligand` takes seconds.
 */
export function tileProgressSummary(rollup: TileProgressRollup): string {
  const settled = rollup.done + rollup.cached;
  const parts = [`${settled} of ${rollup.total} tiles settled`];
  if (rollup.active > 0) parts.push(`${rollup.active} running`);
  if (rollup.failed > 0) parts.push(`${rollup.failed} failed`);
  return parts.join(" · ");
}
